import React, { useState, useEffect } from 'react';
import useAxios from '../utils/useAxios';
import Swal from 'sweetalert2';
import {
  FaCalendarAlt,
  FaSearch,
  FaCheckCircle,
  FaTimesCircle,
  FaDownload,
  FaBuilding
} from 'react-icons/fa';
import { useSidebar } from '../context/SidebarContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import styles from '../styles/HeureSupplementairePage';
import OrganisationSelector from '../components/OrganisationSelector';
import { useAuth } from '../context/AuthContext';



const HeureSupplementairePage = () => {
  const [heures, setHeures] = useState([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ totalPages: 1 });
  const [selectedDate, setSelectedDate] = useState(''); 
  const [selectedEtat, setSelectedEtat] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const { sidebarVisible } = useSidebar();
  const axiosInstance = useAxios();
  const { user } = useAuth();
  // États pour la gestion des organisations
  const [selectedOrganisation, setSelectedOrganisation] = useState('');
  const [currentOrganisation, setCurrentOrganisation] = useState(null);





  const fetchHeures = async (targetPage = 1, orgId = selectedOrganisation) => {
    if (!orgId) return; 
    
    setLoading(true);
    try {
      const params = {
        page: targetPage,
        ...(selectedDate && { date: selectedDate }),  
        ...(selectedEtat && { etat: selectedEtat }),
        ...(search && { search }),
        ...(orgId && { organisation: orgId }),
      };

      const res = await axiosInstance.get('/pointage/heures-supplementaires/', { params });
      setHeures(res.data.results || res.data);
      setPagination({
        totalPages: Math.ceil(res.data.count / 10) || 1,
      });
      setPage(targetPage);
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: "Impossible de charger les heures supplémentaires.",
      });
    } finally {
      setLoading(false);
    }
  };


  // Gestionnaire de changement d'organisation
  const handleOrganisationChange = (orgId, org) => {
    setSelectedOrganisation(orgId);
    setCurrentOrganisation(org);
    fetchHeures(1, orgId);
  };

  useEffect(() => {
    if (selectedOrganisation) {
      fetchHeures(1);
    }
  }, [selectedDate, selectedEtat, search, selectedOrganisation]);

  const fetchAllHeures = async () => {
    try {
      let allHeures = [];
      let currentPage = 1;
      let hasMore = true;

      while (hasMore) {
        const params = new URLSearchParams({
          page: currentPage,
          ...(selectedDate && { date: selectedDate }),
          ...(selectedEtat && { etat: selectedEtat }),
          ...(search && { search }),
          ...(selectedOrganisation && { organisation: selectedOrganisation }),
        });
        
        const response = await axiosInstance.get(`/pointage/heures-supplementaires/?${params}`);
        allHeures = [...allHeures, ...(response.data.results || response.data)];
        hasMore = currentPage * 10 < response.data.count;
        currentPage++;
      }
      return allHeures;
    } catch (err) {
      console.error("Erreur lors de la récupération des heures", err);
      return [];
    }
  };

  const handleExportPDF = async () => {
    setLoading(true);
    try {
      const doc = new jsPDF();
      doc.setFontSize(18);
      doc.text(`Rapport des heures supplémentaires - ${currentOrganisation?.nom || ''}`, 14, 20);

      const allHeures = await fetchAllHeures();

      const tableColumn = ["Employé", "Activité", "Date", "Heures", "Montant (FCFA)", "Statut"];
      const tableRows = [];

      allHeures.forEach((h) => {
        tableRows.push([
          `${h.employe?.first_name || ''} ${h.employe?.last_name || ''}`,
          h.activite?.nom || 'N/A',
          h.date ? new Date(h.date).toLocaleDateString() : '-',
          parseFloat(h.heures).toFixed(2) + 'h',
          parseFloat(h.montant).toLocaleString(),
          h.etat === 'approuve' ? 'Approuvé' : h.etat === 'rejete' ? 'Rejeté' : 'En attente'
        ]);
      });

      doc.setFontSize(12);
      doc.text(
        `Total: ${allHeures.length} | Approuvées: ${allHeures.filter(h => h.etat === 'approuve').length} | Rejetées: ${allHeures.filter(h => h.etat === 'rejete').length}`,
        14,
        30
      );

      autoTable(doc, { 
        head: [tableColumn], 
        body: tableRows, 
        startY: 40, 
        styles: { fontSize: 10 }, 
        headStyles: { fillColor: [27, 1, 155] } 
      });
      doc.save(`heures_supplementaires_${new Date().toLocaleDateString().replace(/\//g, '-')}_${currentOrganisation?.nom || ''}.pdf`);
    } catch (err) {
      console.error("Erreur lors de la génération du PDF", err);
    } finally {
      setLoading(false);
    }
  };

  const renderEtatBadge = (etat) => {
    switch (etat) {
      case 'approuve':
        return <span className="badge bg-success rounded-pill">Approuvé</span>;
      case 'rejete':
        return <span className="badge bg-danger rounded-pill">Rejeté</span>;
      default:
        return <span className="badge bg-warning text-dark rounded-pill">En attente</span>;
    }
  };

  const handleDecision = async (id, etat) => {
    try {
      await axiosInstance.patch(`/pointage/heures-supplementaires/${id}/`, { etat });
      Swal.fire({
        icon: 'success',
        title: 'Succès',
        text: `La demande a été ${etat === 'approuve' ? 'approuvée' : 'rejetée'} avec succès.`,
        timer: 2000,
        showConfirmButton: false,
      });
      fetchHeures(page);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: "Impossible de mettre à jour le statut de la demande.",
      });
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchHeures(newPage);
    }
  };



  return (
    <div style={{...styles.container, marginLeft: sidebarVisible ? '250px' : '0'}}>

      <div className="text-center mb-3">
        <h3 style={styles.header}>Gestion des Heures Supplémentaires</h3>
        <hr style={styles.hr} />
      </div>
      {/* Header avec sélecteur d'organisation réutilisable */}
      <div style={{ 
        backgroundColor: '#fff', 
        padding: '20px', 
        borderRadius: '12px', 
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        marginBottom: '30px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <OrganisationSelector 
          onOrganisationChange={handleOrganisationChange}
          selectedOrganisation={selectedOrganisation}
        />

        {currentOrganisation && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ color: '#666', fontSize: '14px' }}>
              Heures supplémentaires pour cette organisation
            </span>
          </div>
        )}
      </div>

      {currentOrganisation ? (
        <>
          <div style={styles.filterContainer}>
            <div style={styles.filterGroup}>
              <FaCalendarAlt style={styles.filterIcon} />
              <input
                type="date"
                className="form-control"
                style={styles.searchBar}
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            </div>
            <div style={styles.filterGroup}>
              <select
                className="form-control"
                style={styles.select}
                value={selectedEtat}
                onChange={(e) => setSelectedEtat(e.target.value)}
              >
                <option value="">Tous les états</option>
                <option value="en_attente">En attente</option>
                <option value="approuve">Approuvé</option>
                <option value="rejete">Rejeté</option>
              </select>
            </div>
            <div style={{ ...styles.filterGroup, flexGrow: 1 }}>
              <FaSearch style={styles.filterIcon} />
              <input
                type="text"
                className="form-control"
                style={styles.searchBar}
                placeholder="Rechercher un employé..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {(user?.is_super_admin || user?.permissions?.includes("EXPORT_PDF")) && (
            <div>
              <button
                onClick={handleExportPDF}
                disabled={loading}
                style={styles.exportButton}
              >
                <FaDownload /> {loading ? 'Génération...' : 'Exporter PDF'}
              </button>
            </div>
            )}
          </div>

          <div className="card shadow-sm mb-4" style={{ borderRadius: '15px', border: 'none' }}>
            <div className="card-body table-responsive p-0">
              <table className="table table-hover text-center align-middle m-0">
                <thead>
                  <tr style={styles.tableHeader}>
                    <th>Employé</th>
                    <th>Activité</th>
                    <th>Date</th>
                    <th>Heures</th>
                    <th>Montant (FCFA)</th>
                    <th>Statut</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="7" className="text-muted py-4">Chargement en cours...</td></tr>
                  ) : heures.length > 0 ? (
                    heures.map((row) => (
                      <tr key={row.id}>
                        <td>{row.employe?.first_name || 'N/A'} {row.employe?.last_name || ''}</td>
                        <td>{row.activite?.nom || 'N/A'}</td>
                        <td>{new Date(row.date).toLocaleDateString()}</td>
                        <td>{parseFloat(row.heures).toFixed(2)}h</td>
                        <td>{parseFloat(row.montant).toLocaleString()}</td>
                        <td>{renderEtatBadge(row.etat)}</td>
                        <td>
                          <div className="d-flex justify-content-center">
                            {(user?.is_super_admin || user?.permissions?.includes("APPROVE_HEURESUP")) && (
                            <button
                              style={{ ...styles.actionButton, ...styles.approveBtn, opacity: row.etat === 'approuve' ? 0.6 : 1 }}
                              onClick={() => row.etat !== 'approuve' && handleDecision(row.id, 'approuve')}
                              disabled={row.etat === 'approuve'}
                            >
                              <FaCheckCircle style={{ marginRight: '5px' }} /> Approuver
                            </button>
                            )}
                            {(user?.is_super_admin || user?.permissions?.includes("REJECT_HEURESUP")) && (
                            <button
                              style={{ ...styles.actionButton, ...styles.rejectBtn, opacity: row.etat === 'rejete' ? 0.6 : 1 }}
                              onClick={() => row.etat !== 'rejete' && handleDecision(row.id, 'rejete')}
                              disabled={row.etat === 'rejete'}
                            >
                              <FaTimesCircle style={{ marginRight: '5px' }} /> Rejeter
                            </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="7" className="text-muted py-4">Aucune heure supplémentaire trouvée.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="card-footer d-flex justify-content-center mt-3 bg-transparent border-top-0">
                <nav>
                  <ul className="pagination mb-0">
                    <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => handlePageChange(1)} disabled={page === 1 || loading} style={page === 1 ? styles.paginationDisabled : styles.paginationButton}>Première</button>
                    </li>
                    <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => handlePageChange(page - 1)} disabled={page === 1 || loading} style={page === 1 ? styles.paginationDisabled : styles.paginationButton}>&laquo;</button>
                    </li>
                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                      let pageNum;
                      if (pagination.totalPages <= 5) pageNum = i + 1;
                      else if (page <= 3) pageNum = i + 1;
                      else if (page >= pagination.totalPages - 2) pageNum = pagination.totalPages - 4 + i;
                      else pageNum = page - 2 + i;

                      return (
                        <li key={pageNum} className={`page-item ${page === pageNum ? 'active' : ''}`}>
                          <button className="page-link" onClick={() => handlePageChange(pageNum)} disabled={loading} style={page === pageNum ? { ...styles.paginationButton, ...styles.paginationActive } : styles.paginationButton}>{pageNum}</button>
                        </li>
                      );
                    })}
                    <li className={`page-item ${page === pagination.totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => handlePageChange(page + 1)} disabled={page === pagination.totalPages || loading} style={page === pagination.totalPages ? styles.paginationDisabled : styles.paginationButton}>&raquo;</button>
                    </li>
                    <li className={`page-item ${page === pagination.totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => handlePageChange(pagination.totalPages)} disabled={page === pagination.totalPages || loading} style={page === pagination.totalPages ? styles.paginationDisabled : styles.paginationButton}>Dernière</button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="text-center py-5">
          <p className="text-muted">Chargement des organisations...</p>
        </div>
      )}
    </div>
  );
};

export default HeureSupplementairePage;